<template>
    <div class="d-flex flex-column justify-center align-center fill-height py-8 overflow-hidden">
        <v-card v-if="!allInfoLoaded">
            <v-skeleton-loader
                type="card, list-item-two-line, divider, list-item-avatar-three-line, divider, list-item-avatar-three-line, divider, actions"
                width="350"></v-skeleton-loader>
        </v-card>
        <v-card v-else width="350">
            <v-card-title class="justify-center align-center nuvolos-header">
                <v-img max-width="200" max-height="22" class="py-5" src="@/assets/Nuvolos-transparent.svg" />
            </v-card-title>

            <template v-if="validInvitation && !flow2">
                <h2 class="secondary--text text-center text-h6 mt-5">Join Nuvolos</h2>
                <v-card-text>You have received invitation(s) to Nuvolos. Please review the grants and continue if you wish to accept.</v-card-text>
                <v-subheader>REVIEW GRANTS</v-subheader>
                <v-divider></v-divider>

                <template v-if="resourcePoolRole">
                    <v-subheader>Resource pool</v-subheader>
                    <v-card-text class="pt-0">
                        <v-card flat class="grey lighten-3">
                            <v-card-text class="d-flex align-center">
                                <b>{{ resourcePoolRole.ResourcePool }}</b>
                                <v-spacer></v-spacer>
                                <v-chip small outlined color="primary">
                                    {{ resourcePoolRole.Name }}
                                </v-chip>
                            </v-card-text>
                        </v-card>
                    </v-card-text>
                    <v-divider></v-divider>
                </template>

                <template v-if="orgRole">
                    <v-subheader>Organization</v-subheader>
                    <v-card-text class="pt-0">
                        <v-card flat class="grey lighten-3">
                            <v-card-text class="d-flex align-center">
                                <b>{{ orgRole.Organization }}</b>
                                <v-spacer></v-spacer>
                                <v-chip small outlined color="primary">
                                    {{ organizationRole(orgRole.Name).label }}
                                </v-chip>
                            </v-card-text>
                        </v-card>
                    </v-card-text>
                    <v-divider></v-divider>
                </template>

                <template v-if="spaceRole">
                    <v-subheader>Space</v-subheader>
                    <v-card-text class="pt-0">
                        <v-card flat class="grey lighten-3">
                            <v-card-text class="d-flex align-center">
                                <b>{{ spaceRole.Space }}</b>
                                <v-spacer></v-spacer>
                                <v-chip v-if="spaceRole" small outlined color="primary">
                                    {{ spaceRole.Name }}
                                </v-chip>
                            </v-card-text>
                        </v-card>
                    </v-card-text>
                    <v-divider />
                </template>

                <template v-if="spaceInvites.length > 0">
                    <v-subheader>Space</v-subheader>
                    <v-card-text class="pt-0">
                        <v-card flat class="grey lighten-3">
                            <v-card-text class="d-flex align-center">
                                <b>{{ spaceInvites[0].Space }}</b>
                            </v-card-text>
                            <v-divider></v-divider>
                            <template v-if="spaceInvites.length > 0">
                                <div v-for="(r, index) in spaceInvites" :key="`grants-${index}`">
                                    <v-list-item>
                                        <v-list-item-content>
                                            <v-list-item-subtitle v-for="(value, name) in user_pretty(r)" :key="name" class="text-truncate">
                                                {{ name }}:
                                                <b class="secondary--text">{{ value }}</b>
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                    <v-divider v-if="index < spaceInvites.length - 1"></v-divider>
                                </div>
                            </template>
                        </v-card>
                    </v-card-text>
                    <v-divider />
                </template>

                <v-card-text>
                    <router-view></router-view>
                </v-card-text>
            </template>

            <v-card-text v-if="!validInvitation" class="pt-5">
                <v-alert dense text type="error">We are sorry but this invitation is no longer valid.</v-alert>
                <v-card-actions>
                    <v-btn elevation="0" block color="primary" :to="{ name: 'login' }">Sign in to Nuvolos</v-btn>
                </v-card-actions>
            </v-card-text>

            <v-card-text v-if="flow2" class="pt-5">
                <v-alert dense text type="error">
                    A Nuvolos account with {{ email }} already exists. Please log in with that account to accept this invitation.
                </v-alert>
                <v-card-actions>
                    <v-btn elevation="0" block color="primary" @click="logout()">Sign in to Nuvolos</v-btn>
                </v-card-actions>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { organizationRole } from '@/utils'

export default {
    name: 'invitationIndex',
    components: {},
    data() {
        return {
            validInvitation: false,
            roles: '',
            invitationStatus: '',
            loadingInvitation: true,
            isTrial: false,
            emailIsRegistered: false,
            email: '',
            flow2: false
        }
    },
    created() {
        this.getInvitation()
    },
    computed: {
        allInfoLoaded() {
            return !this.loadingInvitation && !this.$auth.loading && !this.fetchingUserInfo
        },
        orgRole() {
            return this.roles.find(role => role.role_type === 'OrgRoleType')
        },
        spaceRole() {
            return this.roles.find(role => role.role_type === 'SpaceRoleType')
        },
        spaceInvites() {
            return this.roles.filter(role => role.role_type === 'InstanceRoleType')
        },
        resourcePoolRole() {
            return this.roles.find(role => role.role_type === 'ResourcePoolRoleType')
        },
        ...mapState(['userInfo', 'fetchingUserInfo'])
    },
    methods: {
        organizationRole,
        async getInvitation() {
            try {
                const { data } = await this.$axios.get(`/invitations/describe/${this.$route.params.invid}`)
                this.roles = data.roles
                this.validInvitation = data.valid === 'True'
                this.email = data.requestee_email

                const { data: loginMethods } = await this.$axios.post('/users/login_methods', { email: this.email })
                if (loginMethods?.methods.length) {
                    this.emailIsRegistered = true
                } else {
                    this.emailIsRegistered = false
                }

                this.$store.dispatch('userStore/setSignupUserDetails', { email: this.email })
                this.isTrial = data.trid !== undefined && data.trid !== null
                this.invitationStatus = data.status_name
            } catch (error) {
                if (error?.response?.status >= 500) {
                    this.$store.dispatch('showGlobalDialog', {
                        dialogTitle: 'Temporary issue',
                        dialogText: 'Something went wrong, please try to refresh this page in a few minutes.',
                        dialogAction: ['reload']
                    })
                }
                this.validInvitation = false
            }
            this.loadingInvitation = false
        },
        user_pretty(r) {
            const prettyInvite = {}
            if (r.Instance) {
                prettyInvite.Instance = r.Instance
            }
            if (r.Name) {
                prettyInvite.Role = r.role_type === 'ResourcePoolRoleType' ? `RESOURCE POOL ${r.Name}` : r.Name
            }
            return prettyInvite
        },
        loginAndReturn() {
            localStorage.setItem('nvLastEmail', this.email)
            this.$router.push({ name: 'to-login', query: { targetUrl: this.$route.path } })
        },
        logout() {
            this.$auth.logout()
        }
    },
    watch: {
        allInfoLoaded() {
            if (!this.validInvitation) return

            if (this.userInfo) {
                // User is logged in with Nuvolos account - take to inv accept
                console.log('invitation flow 1')
                this.$router.push({
                    name: 'invitation-authenticated',
                    params: { invid: this.$route.params.invid }
                })
            } else {
                if (this.$auth.isAuthenticated) {
                    if (this.emailIsRegistered) {
                        // The user does not have a Nuvolos account, but is authenticated. The inv was sent to an existing account
                        // The user cannot register with this invitation because we always take the email for the new account from the invitation
                        console.log('invitation flow 2')
                        this.flow2 = true
                    } else {
                        // The user does not have a Nuvolos account, but is authenticated. The inv was sent to an email not belonging to a Nuvolos account (account link screen)
                        console.log('invitation flow 3')
                        // At this point a new registration must happen, user can not accept this invitation without registration first
                        this.$router.push({ name: 'invitation-authenticated', params: { invid: this.$route.params.invid } })
                    }
                } else {
                    if (this.emailIsRegistered) {
                        // The user does not have a Nuvolos account, and is not authenticated. The inv was sent to an existing account
                        console.log('invitation flow 4')
                        this.loginAndReturn()
                    } else {
                        // The user does not have a Nuvolos account, and is not authenticated. The inv was sent to an email not belonging to a Nuvolos account, allow all choices (Google, SATOSA, email + pwd)
                        // This is the default view
                        console.log('invitation flow 5')
                    }
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@/sass/signup.scss';
</style>
